import { useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Dropdown, Empty, List, Popover } from "antd";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { HomeContext } from "../containers/Home";
import { latestReleaseNote } from "../content/release-notes";
import { getLessonLearnedList } from "../services/api-server/lesson_learned";
import {
	getNotifications,
	postNotificationToUsers,
	updateNotificationStatus,
	updateReadStatus,
} from "../services/api-server/notification";
import { getAllUsers } from "../services/api-server/user";
import { getUserStore } from "../stores/userStore";
import { GetAntIcon } from "../utils/ant_icons";
import { socket } from "../utils/socket";
import { getDuration } from "../utils/utils";

export default function NotificationComponent() {
	const context: any = useContext(HomeContext);

	const userStore = getUserStore();
	const currentUser = userStore.getCurrentUser();
	const userEmail = currentUser?.email?.toLowerCase();

	interface Notification {
		_id: string;
		message: string;
		version: string;
		created_on: string;
		receipients: Array<any>;
	}

	const [dropdownNotification, setDropdownNotification] = useState<
		Notification[]
	>([]);
	const [dropdownOpen, setDropdownOpen] = useState(false);

	const [popOverNotification, setPopOverNotification] = useState<any>(null);

	const [alertCount, setAlertCount] = useState<any>(null);

	const navigate = useNavigate();

	const queryClient = useQueryClient();

	const notificationFilter = {
		receipients: {
			$elemMatch: {
				read: false,
				user_email: { $regex: userEmail, $options: "i" },
			},
		},
	};

	const fetchNotifications = () => {
		getNotifications(notificationFilter, { created_on: -1 })
			.then((response: any) => {
				// sort in ascending order
				const dropdownNotification = response;
				console.log("Dropdown notification: ", response);

				setDropdownNotification(dropdownNotification);

				calculateAlertCount(dropdownNotification);

				if (response && response.length > 0) {
					const newestDatabaseDocument = response
						.sort(
							(a: any, b: any) =>
								new Date(b.created_on).getTime() -
								new Date(a.created_on).getTime()
						)
						.filter(
							(notification: any) =>
								notification?.type === "release_notes" &&
								notification?.receipients?.some(
									(recipient: any) =>
										recipient.notified === false &&
										recipient.user_email?.toLowerCase() === userEmail
								)
						)[0];
					setPopOverNotification(newestDatabaseDocument);
				} else {
					setPopOverNotification(null);
				}
			})
			.catch((error: any) => {
				console.error("Error fetching dropdown notifications:", error);
			});
	};

	// Fetch dropdown notifications
	useEffect(() => {
		fetchNotifications();
	}, []);

	//Access socket
	useEffect(() => {
		socket.emit("join_room", {
			room: "notification",
			user: userEmail,
		});

		socket.on(`${userEmail}_notification`, (payload: any) => {
			console.log(`[SOCKET RECEIVED] For: ${userEmail}, Payload:`, payload);
			queryClient.invalidateQueries({ queryKey: ["my_tasks"] });
			queryClient.invalidateQueries({ queryKey: ["reference_data"] });
			queryClient.invalidateQueries({ queryKey: ["reporting-hub-data"] });

			if (payload) {
				const allNotifications = [...dropdownNotification, payload];
				setDropdownNotification(allNotifications);
				calculateAlertCount(allNotifications);
				if (payload.type === "release_notes") {
					setPopOverNotification(payload);
				}
			} else {
				fetchNotifications();
			}
		});

		return () => {
			socket.emit("leave_room", {
				room: "notification",
				user: userEmail,
			});
			socket.off(`${userEmail}_notification`);
		};
	}, [dropdownNotification]);

	// Fetch latest release notes notification
	useEffect(() => {
		const latestVersion = latestReleaseNote?.version;
		if (!latestVersion) return;

		getNotifications({
			type: "release_notes",
			version: latestVersion,
		})
			.then((notifications: any) => {
				// if there are no notifications, post a new one
				if (notifications.length === 0) {
					postNotificationToUsers(
						"Please check out our newest version",
						latestVersion
					).catch((error) => {
						console.error("Error posting notification: ", error);
					});
				}
			})
			.catch((error) => {
				console.error("Error fetching notifications:", error);
			});
	}, []); // This effect runs once when the component mounts

	const handleUpdateNotificationStatus = async () => {
		try {
			const status = await updateNotificationStatus(userEmail);
			setPopOverNotification(false);
		} catch (error) {
			console.log(error);
		}
	};

	const calculateAlertCount = (notifications: any) => {
		let count = 0;
		notifications?.forEach((notification: any) => {
			if (
				notification?.receipients?.some(
					(recipient: any) =>
						recipient.read === false &&
						recipient.user_email?.toLowerCase() === userEmail
				)
			) {
				count++;
			}
		});
		setAlertCount(count);
	};

	const handleUpdateReadStatus = async (notificationId: any) => {
		try {
			const notification = await updateReadStatus(notificationId, userEmail);

			// Find and remove the notification from the dropdown notification array
			let newDropdownNotification = dropdownNotification.filter(
				(notification: any) => notification._id !== notificationId
			);

			// Find and replace the notification in the dropdown notification array
			// let newDropdownNotification = dropdownNotification.map(
			// 	(_notification: any) =>
			// 		_notification._id === notificationId ? notification : _notification
			// );
			setDropdownNotification(newDropdownNotification);
			calculateAlertCount(newDropdownNotification);
		} catch (error) {
			console.log(error);
		}
	};

	const getLLRecord = async (reportId: any) => {
		const result = await getLessonLearnedList({ _id: reportId });

		if (Array.isArray(result) && result.length > 0) {
			return result[0];
		}

		return null;
	};

	const handleNavigate = async (notification: any) => {
		switch (notification.type) {
			case "release_notes":
				// The drawer shows every release, so the popover isn't needed.
				setPopOverNotification(false);
				context.setWhatsNewOpen(true);
				break;
			case "lesson_learned":
				if (notification?.properties?.status === "Closed") {
					try {
						const activeLLRecord = await getLLRecord(
							notification?.properties?.report_id
						);

						const assignedByEmail = activeLLRecord.assigned_by?.toLowerCase();

						// Get user name from allData
						const userName = await getAllUsers().then((users: any) => {
							return users.find((user: any) => {
								return user.email?.toLowerCase() === assignedByEmail;
							})?.name;
						});

						if (activeLLRecord) {
							activeLLRecord.project = context?.allData?.projects?.find(
								(project: any) => project._id === activeLLRecord.project
							)?.project_name;
							activeLLRecord.operation_name = context?.allData?.operation?.find(
								(operation: any) =>
									operation._id === activeLLRecord.operation_name
							)?.operation_name;
							activeLLRecord.discipline = context?.allData?.discipline?.find(
								(discipline: any) =>
									discipline._id === activeLLRecord.discipline
							)?.discipline;
							activeLLRecord.origin = context?.allData?.origin?.find(
								(origin: any) => origin._id === activeLLRecord.origin
							)?.origin;
							activeLLRecord.well = context?.allData?.well?.find(
								(well: any) => well._id === activeLLRecord.well
							)?.well_name;
							activeLLRecord.phase = context?.allData?.phase?.find(
								(phase: any) => phase._id === activeLLRecord.phase
							)?.phase_name;
							activeLLRecord.section = context?.allData?.section?.find(
								(section: any) => section._id === activeLLRecord.section
							)?.section;
							activeLLRecord.lesson_learned_type =
								context?.allData?.lesson_learned_type?.find(
									(lesson_learned_type: any) =>
										lesson_learned_type._id ===
										activeLLRecord.lesson_learned_type
								)?.lesson_learned_type;
							activeLLRecord.lesson_learned_classification =
								context?.allData?.lesson_learned_classification?.find(
									(lesson_learned_classification: any) =>
										lesson_learned_classification._id ===
										activeLLRecord.lesson_learned_classification
								)?.lesson_learned_classification;
							activeLLRecord.task = context?.allData?.task?.find(
								(task: any) => task._id === activeLLRecord.task
							)?.task_name;
							activeLLRecord.assigned_by = {
								email: assignedByEmail,
								name: userName,
							};
						}

						navigate(`/lessons-learned/${activeLLRecord._id}`, {
							replace: false,
							state: {
								activeRecord: activeLLRecord,
							},
						});
					} catch (err) {
						console.error("Error fetching LL record:", err);
					}
				}

				break;
			case "my_tasks":
				navigate(
					`/my-tasks?report_type=${notification?.properties?.report_type}&report_id=${notification?.properties?.report_id}`
				);
				break;
		}
	};

	const handleNotificationItemRender = (item: any) => {
		return (
			<div
				style={{
					display: "flex",
					gap: "10px",
					width: "100%",
				}}
				onClick={() => {
					handleUpdateReadStatus(item._id);
					handleNavigate(item);
					setDropdownOpen(false);
				}}
			>
				{/* Notification List Icon */}
				<div
					style={{
						display: "flex",
						alignItems: "center",
						marginLeft: "16px",
					}}
				>
					{/* <Badge
						dot={
							Array.isArray(userReadStatuses) &&
							userReadStatuses.some(
								(status: any) =>
									status.version ===
										item.version &&
									status.read === false
							)
						}
						offset={[-3, 5]}
					> */}
					<span className="notification-list-icon">
						{item.type === "release_notes" && GetAntIcon("notification")}
						{item.type === "lesson_learned" && GetAntIcon("reconciliation")}
						{item.type === "my_tasks" && GetAntIcon("reconciliation")}
					</span>
					{/* </Badge> */}
				</div>
				<div
					style={{
						display: "flex",
						flex: 1,
						flexDirection: "column",
						marginRight: "16px",
					}}
				>
					{item.type === "release_notes" && (
						<p style={{ margin: "0px", fontSize: "14px" }}>
							<strong>Version {item.version} </strong>
							{item.message}
						</p>
					)}
					{(item.type === "lesson_learned" || item.type === "my_tasks") && (
						<>
							<p style={{ margin: "0px", fontSize: "14px" }}>{item.message}</p>
							<p style={{ margin: "0px", fontSize: "14px", color: "#00BEBE" }}>
								{item?.properties?.report_number} {item?.properties?.title}
							</p>
						</>
					)}

					{/* Date and Time */}
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							gap: "8px",
						}}
					>
						<span style={{ color: "var(--comment-box-date-time)" }}>
							{/* get duration between now and item.created_on */}
							{getDuration(item.created_on)}
							{/* {new Date(item.created_on).toLocaleString()} */}
						</span>
						{item?.receipients?.some(
							(recipient: any) =>
								recipient.read === false &&
								recipient.user_email?.toLowerCase() === userEmail
						) && (
							<Button
								type="link"
								onClick={async (event) => {
									event.stopPropagation();
									try {
										handleUpdateReadStatus(item._id);
									} catch (error) {
										console.error(error);
									}
								}}
							>
								Mark as read
							</Button>
						)}
					</div>
				</div>
			</div>
		);
	};

	return (
		<div style={{ position: "relative" }}>
			<Dropdown
				placement="bottomRight"
				open={dropdownOpen}
				onOpenChange={setDropdownOpen}
				dropdownRender={() => (
					<div style={{ width: "100%" }}>
						<div
							style={{
								padding: "8px 0px",
								maxWidth: "400px",
								boxShadow:
									"0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)",
								borderRadius: "8px",
								overflow: "auto",
							}}
						>
							<List
								style={{
									maxHeight: "460px",
									overflow: "auto",
								}}
								dataSource={dropdownNotification}
								renderItem={(item, index) => (
									// Notification List Item
									<List.Item key={index} className="notification-list-item">
										{handleNotificationItemRender(item)}
									</List.Item>
								)}
								locale={{
									emptyText: (
										<Empty
											image={Empty.PRESENTED_IMAGE_SIMPLE}
											description="No notifications."
										/>
									),
								}}
							/>
						</div>
					</div>
				)}
				trigger={["click"]}
			>
				<Badge
					size="small"
					count={alertCount}
					styles={{
						indicator: {
							fontSize: 10,
							lineHeight: "16px",
						},
					}}
				>
					<Button shape="circle" icon={GetAntIcon("bell")} />
				</Badge>
			</Dropdown>
			{/* Popover */}

			<Popover
				placement="bottomLeft"
				open={popOverNotification}
				onOpenChange={(open) => {
					if (!open) {
						handleUpdateNotificationStatus();
					}
				}}
				title={
					<div
						style={{
							maxWidth: "300px",
							padding: "4px",
							display: "flex",
							flexDirection: "column",
							alignItems: "start",
							gap: "8px",
						}}
					>
						<div
							style={{
								fontSize: "14px",
								fontWeight: "normal",
							}}
						>
							<div style={{ display: "flex", gap: "8px" }}>
								<span className="notification-list-icon">
									{GetAntIcon("notification")}
								</span>
								<span style={{ fontWeight: "bold" }}>
									{`We just released Version ${popOverNotification?.version}!`}
								</span>
							</div>
							<div
								style={{
									fontSize: "14px",
									fontWeight: "normal",
								}}
							>
								{popOverNotification?.message}
							</div>
							<Button
								type="link"
								style={{ float: "right" }}
								onClick={async () => {
									try {
										handleUpdateNotificationStatus();
										handleUpdateReadStatus(popOverNotification._id);
										// Hide it now, not when the update returns, so it doesn't
										// sit over the drawer.
										setPopOverNotification(false);
										context.setWhatsNewOpen(true);
									} catch (error) {
										console.log(error);
									}
								}}
							>
								Read more
							</Button>
						</div>
					</div>
				}
			></Popover>
		</div>
	);
}
